import React from 'react';
import { Link } from 'react-router-dom';
import OptimizedImage from '../components/OptimizedImage';
import SEOHead from '../components/SEOHead';
import { imageConfig, altTexts } from '../utils/imageConfig';

const Services = () => {
  return (
    <div className="services">
      <SEOHead 
        title="Our Services - Saner Consulting | Business Transformation & AI Implementation"
        description="Comprehensive business transformation services including AI implementation, digital transformation, portfolio management, and transformation assurance. Expert consultancy for businesses of all sizes."
        keywords="business transformation services, AI implementation, digital transformation, portfolio management, transformation assurance, consulting services, UK"
        canonical="https://sanerconsulting.co.uk/services"
      />
      {/* Hero Section */}
      <section className="hero section">
        <div className="container">
          <div className="grid grid-2" style={{ alignItems: 'center', gap: '3rem' }}>
            <div className="hero-content fade-in-up">
              <h1 className="hero-title">Our Services</h1>
              <p className="subtitle fade-in-up" style={{animationDelay: '0.2s'}}>
                Modernise technology. Accelerate transformation. Deliver results.
              </p>
            </div>
            <div className="hero-image fade-in-up" style={{animationDelay: '0.3s'}}>
              <OptimizedImage
                src={imageConfig.hero.servicesHero}
                alt="Comprehensive business transformation and consulting services"
                width="100%"
                height="400px"
                className="hero-main-image"
                style={{ objectFit: 'cover', aspectRatio: '16/9' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">Core Service Offerings</h2>
          <div className="services-grid">
            <div className="service-card">
              <OptimizedImage
                src={imageConfig.services.dataAnalytics}
                alt="Transformation Capabilities"
                width="100%"
                height="200px"
                className="service-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Transformation Capabilities</h3>
              <p className="card-description">
                Building capabilities for sustainable change and growth.
              </p>
              <ul className="service-features">
                <li>Digital maturity assessment</li>
                <li>Technology roadmap development</li>
                <li>Digital operating model design</li>
                <li>Innovation strategy formulation</li>
              </ul>
            </div>

            <div className="service-card">
              <OptimizedImage
                src={imageConfig.technology.delivery}
                alt="Portfolio & Programme Delivery"
                width="100%"
                height="200px"
                className="service-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Portfolio & Programme Delivery</h3>
              <p className="card-description">
                Managing complex portfolios and programmes effectively through proven methodologies.
              </p>
              <ul className="service-features">
                <li>Portfolio strategy development</li>
                <li>Programme governance frameworks</li>
                <li>Benefits realization tracking</li>
                <li>Risk and issue management</li>
              </ul>
            </div>

            <div className="service-card">
              <OptimizedImage
                src={imageConfig.services.changeManagementAlt}
                alt={altTexts.services.changeManagement}
                width="100%"
                height="200px"
                className="service-image"
              />
              <h3 className="card-title">Transformation Assurance</h3>
              <p className="card-description">
                Ensuring transformations deliver intended outcomes.
              </p>
              <ul className="service-features">
                <li>Transformation assurance frameworks</li>
                <li>Quality assurance processes</li>
                <li>Performance monitoring</li>
                <li>Outcome validation</li>
              </ul>
            </div>

            <div className="service-card">
              <OptimizedImage
                src={imageConfig.services.enterpriseSolutions}
                alt="Helping Large & Small Businesses"
                width="100%"
                height="200px"
                className="service-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Helping Large & Small Businesses</h3>
              <p className="card-description">
                Tailored solutions for businesses of all sizes, from startups to enterprises.
              </p>
              <ul className="service-features">
                <li>Scalable transformation approaches</li>
                <li>Budget-conscious solutions</li>
                <li>SME-focused methodologies</li>
                <li>Enterprise-grade capabilities</li>
              </ul>
            </div>

            <div className="service-card">
              <OptimizedImage
                src={imageConfig.services.cloudTransformation}
                alt="Cloud transformation and data migration services"
                width="100%"
                height="200px"
                className="service-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Cloud Transformation</h3>
              <p className="card-description">
                Accelerate innovation and reduce costs through strategic cloud adoption and modern infrastructure solutions.
              </p>
              <ul className="service-features">
                <li>Cloud strategy and planning</li>
                <li>Migration and modernization</li>
                <li>Cloud-native development</li>
                <li>Security and compliance</li>
              </ul>
            </div>

            <div className="service-card">
              <OptimizedImage
                src={imageConfig.services.analyticsDashboard}
                alt="Performance Improvement and Optimization"
                width="100%"
                height="200px"
                className="service-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Performance Improvement</h3>
              <p className="card-description">
                Drive operational excellence. Continuous improvement.
              </p>
              <ul className="service-features">
                <li>Performance measurement systems</li>
                <li>Benchmarking and analysis</li>
                <li>Continuous improvement programs</li>
                <li>Operational excellence frameworks</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Typical Roles We Play */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Roles We Help You Define, Roll Out & Embed</h2>
          <p className="section-description">
            Our experts integrate seamlessly into your team, providing leadership and expertise where you need it most.
          </p>
          <div className="roles-grid">
            <div className="role-card">
              <div className="role-image-placeholder">
                <OptimizedImage
                  src={imageConfig.about.changeManagementExpert}
                  alt="Transformation Portfolio Leader"
                  className="role-image"
                />
              </div>
              <h3 className="card-title">Transformation Portfolio Leader</h3>
              <p className="card-description service-description-long">
                Leading strategic transformation initiatives across your entire portfolio. Ensuring alignment with business objectives and maximizing value delivery.
              </p>
              <p className="card-description service-description-short">
                Leading strategic transformation initiatives and value delivery.
              </p>
            </div>

            <div className="role-card">
              <div className="role-image-placeholder">
                <OptimizedImage
                  src={imageConfig.about.aiStrategyConsultant}
                  alt="Technology / Digital Transformation Leader"
                  className="role-image"
                />
              </div>
              <h3 className="card-title">Technology / Digital Transformation Leader</h3>
              <p className="card-description service-description-long">
                Driving technology modernization and digital innovation. Bridging the gap between business needs and technical solutions.
              </p>
              <p className="card-description service-description-short">
                Driving technology modernization and digital innovation.
              </p>
            </div>

            <div className="role-card">
              <div className="role-image-placeholder">
                <OptimizedImage
                  src={imageConfig.about.digitalStrategyLeader}
                  alt="Portfolio Management Office Leader"
                  className="role-image"
                />
              </div>
              <h3 className="card-title">Portfolio Management Office Leader</h3>
              <p className="card-description">
                Leading the Portfolio Management Office to drive governance and oversight. Ensuring optimal resource allocation and strategic alignment.
              </p>
            </div>

            <div className="role-card">
              <div className="role-image-placeholder">
                <OptimizedImage
                  src={imageConfig.services.transformationAssurance}
                  alt="Transformation Assurance Leader"
                  width="100%"
                  height="200px"
                  className="role-image"
                  style={{ objectFit: 'cover', objectPosition: 'center 5%', aspectRatio: '1/1' }}
                />
              </div>
              <h3 className="card-title">Transformation Assurance Leader</h3>
              <p className="card-description">
                Challenging transformation delivery to ensure a safe and effective return on investment. Providing independent oversight and validation.
              </p>
            </div>

            <div className="role-card">
              <div className="role-image-placeholder">
                <OptimizedImage
                  src={imageConfig.about.aiTransformationCoach}
                  alt="AI Transformation Coach"
                  className="role-image"
                />
              </div>
              <h3 className="card-title">AI Transformation Coach</h3>
              <p className="card-description">
                Guiding the organisation while embedding emerging new ways of working. Driving cultural changes demanded by AI adoption.
              </p>
            </div>

            <div className="role-card">
              <div className="role-image-placeholder">
                <OptimizedImage
                  src={imageConfig.about.transformationAdvisor}
                  alt="Transformation Advisor"
                  className="role-image"
                />
              </div>
              <h3 className="card-title">Transformation Advisor</h3>
              <p className="card-description">
                Helping leaders adapt their strategies to continuously adopt and exploit every opportunity. Navigating the rapid developments in AI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emerging Technology Solutions */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">Cutting-Edge Technology Solutions</h2>
          <p className="subtitle">
            In an era of unprecedented technological advancement, we help organizations embrace innovation 
            to achieve transformational breakthroughs and sustainable competitive advantages.
          </p>
          
          <div className="tech-solutions-grid">
            <div className="tech-solution-card">
              <div className="tech-image-placeholder">
                <OptimizedImage
                  src={imageConfig.technology.nextGenerationComputing}
                  alt="Next-Generation Computing"
                  width="100%"
                  height="150px"
                  className="tech-image"
                  style={{ objectFit: 'cover', aspectRatio: '5/3' }}
                />
              </div>
              <h3 className="card-title">Next-Generation Computing</h3>
              <p className="card-description">
                Harness quantum computing, edge processing, and advanced computational models to solve 
                complex business challenges that were previously impossible to address.
              </p>
              <ul className="tech-benefits">
                <li>Quantum-inspired optimization algorithms</li>
                <li>Edge computing implementation</li>
                <li>High-performance computing solutions</li>
              </ul>
            </div>
            
            <div className="tech-solution-card">
              <div className="tech-image-placeholder">
                <OptimizedImage
                  src={imageConfig.technology.nlpProcessing}
                  alt="Digital-Physical Convergence"
                  width="100%"
                  height="150px"
                  className="tech-image"
                />
              </div>
              <h3 className="card-title">Digital-Physical Convergence</h3>
              <p className="card-description">
                Bridge the gap between digital and physical worlds through IoT, digital twins, 
                and immersive technologies that create new business paradigms.
              </p>
              <ul className="tech-benefits">
                <li>Digital twin development</li>
                <li>IoT ecosystem integration</li>
                <li>Augmented reality solutions</li>
              </ul>
            </div>
            
            <div className="tech-solution-card">
              <div className="tech-image-placeholder">
                <OptimizedImage
                  src={imageConfig.technology.purposefulInnovation}
                  alt="Purposeful Innovation"
                  width="100%"
                  height="150px"
                  className="tech-image"
                  style={{ objectFit: 'cover', aspectRatio: '5/3' }}
                />
              </div>
              <h3 className="card-title">Purposeful Innovation</h3>
              <p className="card-description">
                Assure transformation efforts. Gain delivery insights.
              </p>
              <ul className="tech-benefits">
                <li>Innovation strategy frameworks</li>
                <li>Impact measurement systems</li>
                <li>Sustainable technology adoption</li>
              </ul>
            </div>
            
            <div className="tech-solution-card">
              <div className="tech-image-placeholder">
                <OptimizedImage
                  src={imageConfig.technology.scienceTech}
                  alt="Science-Tech for Social Impact"
                  width="100%"
                  height="150px"
                  className="tech-image"
                  style={{ objectFit: 'cover', aspectRatio: '5/3' }}
                />
              </div>
              <h3 className="card-title">Science-Tech for Social Impact</h3>
              <p className="card-description">
                Leverage advanced scientific technologies to address healthcare challenges, 
                environmental issues, and global societal needs.
              </p>
              <ul className="tech-benefits">
                <li>Healthcare technology solutions</li>
                <li>Environmental impact systems</li>
                <li>Social good technology platforms</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Industry Expertise</h2>
          <div className="grid grid-3">
            <div className="card">
              <h3 className="card-title">Retail Banking</h3>
              <p className="card-description">
                Digital banking transformation, customer experience optimization, regulatory compliance, and fintech innovation.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Corporate & Investment Banking</h3>
              <p className="card-description">
                Investment banking technology, trading systems, clearing & settlement, and capital markets modernization.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Asset Management</h3>
              <p className="card-description">
                Portfolio management systems, investment operations, fund administration, and wealth management platforms.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Insurance</h3>
              <p className="card-description">
                Claims processing optimization, underwriting automation, policy administration, and insurance analytics.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Energy</h3>
              <p className="card-description">
                Oil & gas operations, energy trading systems, renewable energy platforms, and environmental compliance.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Software</h3>
              <p className="card-description">
                Product innovation, scalable architectures, quality assurance, and software engineering management.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Management Consulting</h3>
              <p className="card-description">
                Process optimization, organizational transformation, strategy implementation, and change management.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Public Sector & Government</h3>
              <p className="card-description">
                Digital government services, public sector modernization, citizen experience, and governance frameworks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-dark">
        <div className="container">
          <div className="cta-content">
            <h2 className="section-title">Ready to Transform Your Business?</h2>
            <p className="subtitle">
              Connect with our experts to discuss your transformation goals and discover how we can help you achieve measurable results.
            </p>
            <div className="cta-buttons">
              <Link to="/contact#contact-form" className="btn btn-primary">
                Get Started Today
              </Link>
              <Link to="/services" className="btn btn-secondary">
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;